import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Send, Megaphone, Loader2, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEntitlements } from "@/hooks/use-entitlements";
import { UpgradeOverlay } from "@/components/upgrade-overlay";
import {
  listTeacherClasses, createTeacherAnnouncement,
  listMyTeacherAnnouncements, deleteTeacherAnnouncement,
} from "@/lib/teacher-announcements.functions";

export const Route = createFileRoute("/teacher/announcements")({ component: TeacherAnnouncements });

function TeacherAnnouncements() {
  const ent = useEntitlements();
  const qc = useQueryClient();
  const listClassesFn = useServerFn(listTeacherClasses);
  const listMineFn = useServerFn(listMyTeacherAnnouncements);
  const createFn = useServerFn(createTeacherAnnouncement);
  const deleteFn = useServerFn(deleteTeacherAnnouncement);

  const [classId, setClassId] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");

  const classesQ = useQuery({
    queryKey: ["teacher-classes-for-announce"],
    enabled: ent.teacherNotify,
    queryFn: () => listClassesFn({}),
  });
  const listQ = useQuery({
    queryKey: ["teacher-announcements"],
    enabled: ent.teacherNotify,
    queryFn: () => listMineFn({}),
  });

  const classes = classesQ.data?.classes ?? [];
  const items = listQ.data?.items ?? [];

  const sendMut = useMutation({
    mutationFn: async () => {
      if (!classId) throw new Error("Hãy chọn lớp");
      if (!title.trim() || !body.trim()) throw new Error("Hãy nhập tiêu đề và nội dung");
      return createFn({ data: { class_id: classId, title: title.trim(), body: body.trim() } });
    },
    onSuccess: (res) => {
      toast.success(`Đã gửi thông báo tới lớp ${res.className ?? ""}`);
      setTitle(""); setBody("");
      qc.invalidateQueries({ queryKey: ["teacher-announcements"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!ent.isLoading && !ent.teacherNotify) {
    return (
      <PortalLayout portal="teacher" breadcrumb={[{ label: "Giáo viên" }, { label: "Thông báo" }]}>
        <UpgradeOverlay
          feature="Gửi thông báo cho lớp"
          description="Giáo viên gửi thông báo trực tiếp tới học sinh trong lớp mình dạy."
        />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout portal="teacher" breadcrumb={[{ label: "Giáo viên" }, { label: "Thông báo" }]}>
      <div className="grid lg:grid-cols-3 gap-5">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <Megaphone className="size-4" style={{ color: "var(--portal)" }} />
            Gửi thông báo tới lớp
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Lớp (chỉ lớp bạn đang dạy)</label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger><SelectValue placeholder={classes.length ? "Chọn lớp" : "Bạn chưa có lớp nào"} /></SelectTrigger>
                <SelectContent>
                  {classes.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Tiêu đề thông báo..." value={title} onChange={(e) => setTitle(e.target.value)} />
            <Textarea placeholder="Nội dung thông báo..." rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
            <div className="flex justify-end pt-2 border-t">
              <Button
                size="sm"
                onClick={() => sendMut.mutate()}
                disabled={sendMut.isPending || !classes.length}
                className="gap-1.5"
                style={{ background: "var(--portal)", color: "white" }}
              >
                {sendMut.isPending ? <><Loader2 className="size-4 animate-spin" />Đang gửi...</> : <><Send className="size-4" />Gửi thông báo</>}
              </Button>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-semibold mb-4">Đã gửi</h3>
          {listQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Đang tải...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">Chưa có thông báo nào.</div>
          ) : (
            <ul className="space-y-3">
              {items.map((h: any) => {
                const cls = classes.find((c: any) => c.id === h.class_id);
                return (
                  <li key={h.id} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-tight mb-1">{h.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{h.body}</div>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                          {cls && <Badge variant="outline" className="text-[10px]">{cls.name}</Badge>}
                          <span>{format(new Date(h.created_at), "dd/MM/yyyy HH:mm", { locale: vi })}</span>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!confirm("Xoá thông báo này?")) return;
                          try {
                            await deleteFn({ data: { id: h.id } });
                            toast.success("Đã xoá");
                            qc.invalidateQueries({ queryKey: ["teacher-announcements"] });
                          } catch (e: any) { toast.error(e.message); }
                        }}
                        className="text-muted-foreground hover:text-red-500 transition"
                      ><Trash2 className="size-4" /></button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </motion.div>
      </div>
    </PortalLayout>
  );
}
